import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { RouterFacade } from '@core/shared/data-access';
import { NotificationService } from '@core/shared/util';
import { ArticleSearchQueryField, LoadingStatus } from '@mp/shared/domain';

import {
  AddShoppingCartPosition,
  AddToCartToastComponent,
  CostCenterForSelection,
  ShoppingCartBadgeInfo,
} from '../../common';
import { ShoppingCart } from '../models';
import { ShoppingCartService } from '../services';

import { ShoppingCartActions } from './shopping-cart.actions';
import { selectShoppingCarts, selectShoppingCartsStatus } from './shopping-cart.selectors';

interface SharedSearchParams {
  query?: string;
  field?: ArticleSearchQueryField;
}

@Injectable()
export class ShoppingCartFacade {
  private readonly store$: Store = inject(Store);
  private readonly routerFacade: RouterFacade = inject(RouterFacade);
  private readonly toaster: NotificationService = inject(NotificationService);
  private readonly service: ShoppingCartService = inject(ShoppingCartService);

  private readonly shoppingCartsStatus$: Observable<LoadingStatus> = this.store$.select(selectShoppingCartsStatus);

  readonly shoppingCartsLoading$: Observable<boolean> = this.shoppingCartsStatus$.pipe(
    map((status) => status === LoadingStatus.Loading),
  );

  readonly shoppingCarts$: Observable<ShoppingCart[]> = this.store$.select(selectShoppingCarts);

  private readonly _carts$ = new BehaviorSubject<CostCenterForSelection[]>([]);
  readonly costCenters$ = this._carts$.asObservable();

  private readonly _costCentersLoading$ = new BehaviorSubject(false);
  readonly costCentersLoading$ = this._costCentersLoading$.asObservable();

  readonly shoppingCartBadgeInfo$: Observable<ShoppingCartBadgeInfo> = this.shoppingCarts$.pipe(
    map((shoppingCarts) => this.extractShoppingCartBadgeInfo(shoppingCarts)),
  );

  fetchShoppingCarts(): void {
    this.store$.dispatch(ShoppingCartActions.fetchShoppingCarts());
  }

  requestShoppingCart(shoppingCartId: string): void {
    this.store$.dispatch(ShoppingCartActions.requestShoppingCart({ shoppingCartId }));
  }

  updateShoppingCart(shoppingCartId: string, shoppingCart: Partial<ShoppingCart>): void {
    this.store$.dispatch(ShoppingCartActions.shoppingCartUpdate({ shoppingCartId, shoppingCart }));
  }

  navigateBack(): void {
    this.routerFacade.back();
  }

  navigateBackToArticlesearch(queryParams?: SharedSearchParams): void {
    // NOTE: Setting the query param to null will remove it from the url
    this.routerFacade.navigate((router, route) => router.navigate(['../list'], { relativeTo: route, queryParams }));
  }

  // TODO: Pass these two functions through a store
  fetchCostCenters(): void {
    this._costCentersLoading$.next(true);
    this.service.fetchCostCenters().subscribe({
      next: (costCenters: CostCenterForSelection[]) => {
        this._carts$.next(costCenters);
        this._costCentersLoading$.next(false);
      },
    });
  }

  addArticleToCart(addedToCart: AddShoppingCartPosition): void {
    this.service.addArticleToCart(addedToCart).subscribe({
      next: ({ shoppingCart, position }) => {
        this.toaster.toastFromComponent(AddToCartToastComponent, {
          articleName: position.articleName,
          costCenterName: shoppingCart.costCenter?.name,
          number: shoppingCart.costCenter?.number,
        });
        this.fetchCostCenters();
        // Fetch shopping carts to update the badge info
        this.fetchShoppingCarts();
      },
    });
  }

  private extractShoppingCartBadgeInfo(shoppingCarts: ShoppingCart[]): ShoppingCartBadgeInfo {
    return {
      isMultipleCarts: shoppingCarts.length > 1,
      elementsCount: this.getBadgeInfoElementsCount(shoppingCarts),
    };
  }

  private getBadgeInfoElementsCount(shoppingCarts: ShoppingCart[]): number {
    return shoppingCarts.length === 1 ? shoppingCarts[0].totalPositionCount : shoppingCarts.length;
  }
}
